import { useCallback, useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import DeviceSelector from "components/DeviceSelector";
import GreenhouseControllerSelector from "components/experiment/GreenhouseControllerSelector";
import ExperimentDesigner from "components/experiment/designer/ExperimentDesigner";
import CampaignPanel from "components/experiment/campaign/CampaignPanel";
import CalibrationWhatIf from "components/experiment/whatif/CalibrationWhatIf";
import { useCalibrationCampaign } from "hooks/useCalibrationCampaign";
import { useAuth } from "context/AuthContext";
import { campaignIdFromRunId } from "utils/calibrationCampaign";
import RunContextBar from "components/experiment/RunContextBar";
import RunProgressTimeline from "components/experiment/RunProgressTimeline";
import PumpControlPanel from "components/experiment/PumpControlPanel";
import { usePumpOperations } from "hooks/usePumpOperations";
import RunHistoryList from "components/experiment/RunHistoryList";
import AdHocTelemetryPanel from "components/experiment/AdHocTelemetryPanel";
import LineChartCard from "components/Charts/LineChartCard";
import { useDevices } from "hooks/useDevices";
import { useExperiment, POLL_MS } from "hooks/useExperiment";
import { useCameraDeviceStatus } from "hooks/useCameraDeviceStatus";
import CameraDeviceStatusCard from "components/experiment/CameraDeviceStatusCard";
import CameraImageModal from "components/camera/CameraImageModal";
import { useThumbnailCache } from "hooks/useThumbnailCache";
import GroundTruthSummary from "components/experiment/GroundTruthSummary";
import { exportGreenhouseRun } from "services/greenhouseApi";
import { labelCameraImage, requestTestCapture } from "services/cameraApi";
import {
  countLabelled,
  isLabelled,
  labelSummary,
  nextToReview,
  readerAgreement,
  summarizeOffsets,
  truthOf,
  truthText,
  withTruthRows,
} from "utils/groundTruth";

// One merged chart, not two synced-but-separate ones: decision #3 was that
// the environment (temp/humidity, driven by the greenhouse controller) is
// the independent variable and PadiHAT's own sensor data is the dependent
// one, and both need to sit on the same time axis to actually learn the
// relationship between them - see mergeTimeSeries's own comment for why
// this is a real merge rather than two charts sharing a syncId.
//
// PadiHAT's own lines get a SEPARATE y-axis from the greenhouse's. Found on
// a real soak: PadiHAT's internal temp runs ~10°C above the chamber
// (self-heating from its own electronics, not a sensor bug) - on one shared
// axis, both series get auto-scaled to fit the combined ~23-34°C span and
// each ends up compressed into its own narrow band, reading as a flat line.
// A dual axis lets each series' real variation actually show.
// PadiHAT's own fields (t, h, water_level_cm and its estimate below) get dot: true, unlike the
// much more frequent chamber/pressure-sensor fields - PadiHAT reports on
// the order of once per 15 minutes in the field (its own
// last_reported_interval), so a short run's raw per-instant line for these
// can be down to a single point. LineChartCard defaults every Line to
// dot: false (real perf/clutter concern for the couple-thousand-point
// chamber/pressure series), which made a genuinely-reporting PadiHAT
// invisible on its own chart - the point was still real and found by the
// tooltip's full-array nearest lookup, just never drawn. A visible dot
// means "PadiHAT actually reported here" survives even when there are too
// few points for a connecting line.
//
// dot:true here is a REQUEST, not a guarantee - LineChartCard's
// shouldShowDots() only actually draws dots when this render's data for
// that field is still sparse (see its own comment for why: this project's
// configurable lab-sampling feature can make the water level itself report every
// few seconds during an experiment, and thousands of dots on a dense line
// is exactly the clutter this flag exists to avoid, not create).
const TEMP_LINES = [
  { dataKey: "target_temp_c", name: "Target temp (°C)", color: "#f97316", yAxisId: "temp", strokeDasharray: "4 3" },
  { dataKey: "current_temp_c", name: "Chamber temp (°C)", color: "#ea580c", yAxisId: "temp" },
  { dataKey: "t", name: "PadiHAT internal temp (°C)", color: "#0ea5e9", yAxisId: "padihat_temp", dot: true },
];

const HUMIDITY_LINES = [
  { dataKey: "target_rh_pct", name: "Target RH (%)", color: "#0891b2", yAxisId: "rh", strokeDasharray: "4 3" },
  { dataKey: "current_rh_pct", name: "Chamber RH (%)", color: "#0e7490", yAxisId: "rh" },
  { dataKey: "h", name: "PadiHAT internal RH (%)", color: "#7c3aed", yAxisId: "padihat_rh", dot: true },
];

const WATER_LEVEL_LINES = [
  { dataKey: "water_level_cm", name: "Water level (cm)", color: "#2563eb", yAxisId: "level", dot: true },
  // Our own range estimate (d1_est) as a level: the same offset as the line above minus d1_est instead of d1, so
  // the two share this axis. Only drawn for a device that reports it (see waterLines in the page).
  { dataKey: "water_level_est_cm", name: "Water level, estimated (cm)", color: "#16a34a", yAxisId: "level", dot: true },
  // Same yAxisId as water_level_cm above (both cm), for a direct visual comparison
  // between PadiHAT's ultrasonic reading and the KIT0139 pressure sensor's
  // independent ground-truth reading - see auto-greenhouse-firmware's
  // level_sensor.h and padihat-backend's LevelSensorReading. No dot here -
  // this one reports every 2s, dense enough that a connecting line is
  // always meaningful and dots would just be visual clutter.
  { dataKey: "pressure_level_cm", name: "Water level — pressure (cm)", color: "#9333ea", yAxisId: "level" },
  // The level a person read off a labelled photo (see CameraImageModal): the ground truth the two
  // sensor lines above are judged against. Sparse points, so dots and a connecting line.
  { dataKey: "truth_level_cm", name: "True level - from photo (cm)", color: "#dc2626", yAxisId: "level", dot: true },
];

// The Experiment page's own remembered water-level device - deliberately NOT
// the app-wide "padihat_selected_device" the Camera tab
// writes. Sharing it meant looking at a camera's images on the Camera tab
// mid-experiment overwrote this page's water device with the camera on the
// way back (real bug, found live).
const EXPERIMENT_WATER_STORAGE_KEY = "padihat_experiment_water_device";

export default function ExperimentPage() {
  const { macAddress } = useParams();
  const navigate = useNavigate();
  const { devices, selectedDevice, setSelectedDevice, loading: devicesLoading, refetch: refetchDevices } =
    useDevices({ storageKey: EXPERIMENT_WATER_STORAGE_KEY });
  const routeMac = macAddress ? decodeURIComponent(macAddress) : "";
  // What THIS browser last chose for its next run. While a run is active the
  // run's own water device wins (see `activeWaterMac` below), so every browser
  // and teammate sees the same device.
  const requestedWaterMac = routeMac || selectedDevice;

  // useDevices() only fetches once on mount - without this, RunContextBar's
  // "PadiHAT device" chip freezes at whatever last_seen existed at page
  // load and never updates again, even though the device keeps reporting
  // (a real bug found live: the chip showed "Offline - 3d ago" for a device
  // that was, in fact, online and reporting every few seconds). Every other
  // chip on this page (greenhouse controller, ITC-308, IHC-200, pump)
  // already refreshes via useExperiment's own POLL_MS interval - this just
  // extends that same cadence to the one status source that hook doesn't
  // own.
  useEffect(() => {
    const timer = window.setInterval(() => {
      refetchDevices();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refetchDevices]);

  const experiment = useExperiment(requestedWaterMac);
  const activeWaterMac = experiment.waterDeviceMac;
  const auth = useAuth();
  const isStaff = Boolean(auth?.user?.is_staff);
  // A calibration campaign: the chamber's active one, or the one whose condition run is being viewed in the history.
  const viewedCampaignId = campaignIdFromRunId(experiment.viewedRunId);
  const calibration = useCalibrationCampaign(experiment.selectedMac, viewedCampaignId);
  const [whatIfRefresh, setWhatIfRefresh] = useState(0);
  // The what-if follows the run on the chart: a whole campaign when that run is one of its conditions.
  const chartRunId = experiment.chartWindow?.runId ?? null;
  const chartCampaignId = campaignIdFromRunId(chartRunId);
  const whatIfSource = useMemo(() => {
    if (chartCampaignId) return { campaign: chartCampaignId };
    if (!chartRunId || !experiment.selectedMac) return null;
    return { run: chartRunId, controller: experiment.selectedMac, device: experiment.chartWaterDeviceMac || undefined };
  }, [chartCampaignId, chartRunId, experiment.selectedMac, experiment.chartWaterDeviceMac]);
  const cameraStatus = useCameraDeviceStatus(experiment.cameraDeviceMac);
  const cameraDevice = useMemo(
    () => devices.find((d) => d.mac_address === experiment.cameraDeviceMac) || null,
    [devices, experiment.cameraDeviceMac]
  );
  const waterDevice = useMemo(
    () => devices.find((d) => d.mac_address === activeWaterMac) || null,
    [devices, activeWaterMac]
  );
  // The same full-size image dialog the Camera tab opens; clicking a camera
  // marker on a chart opens the matching image from this run.
  const [openCameraImage, setOpenCameraImage] = useState(null);
  const [exportError, setExportError] = useState("");
  const [exporting, setExporting] = useState(false);

  // Exports whichever run is actually being charted - an explicitly viewed
  // run first, then the active run, then the last one - which is exactly
  // what chartWindow.runId already resolves to (see useExperiment's
  // chartWindow priority), so there's no separate id to compute here.
  const exportableRunId = experiment.chartWindow?.runId ?? null;

  const exportRun = async () => {
    if (!exportableRunId || !experiment.selectedMac) return;
    setExportError("");
    setExporting(true);
    try {
      await exportGreenhouseRun(experiment.selectedMac, exportableRunId, {
        waterDeviceMac: experiment.chartWaterDeviceMac,
      });
    } catch (error) {
      setExportError(error.message);
    } finally {
      setExporting(false);
    }
  };

  const stepMarkers = useMemo(
    () =>
      (experiment.stepBoundaries || []).map((boundary) => ({
        x: boundary.timeNumeric,
        // step_index is now sent by greenhouse_telemetry_series (2026-09-26) -
        // label by step number when available so two consecutive same-state
        // steps (e.g. two holds in a row) don't both just say "RUNNING_HOLD".
        // A campaign's run marks each condition by the chamber target it set.
        label:
          typeof boundary.targetTempC === "number"
            ? `${boundary.targetTempC} °C / ${boundary.targetRhPct} %`
            : typeof boundary.stepIndex === "number" && boundary.stepIndex >= 0
            ? `Step ${boundary.stepIndex + 1}`
            : boundary.stepState,
      })),
    [experiment.stepBoundaries]
  );

  // Ground-truth camera thumbnails, pre-fetched as authenticated blob URLs
  // for every image in the current run (same cache used by the Camera
  // page's gallery) - a typical 15-min-interval run is at most a few dozen
  // images, small enough to prefetch all of them rather than fetch-on-hover.
  const cameraThumbnailUrls = useThumbnailCache(experiment.cameraImages || []);

  // The pump's operations, listed (and flagged when one needs review) at the bottom of the Pump card.
  const pumpOperations = usePumpOperations(experiment.selectedMac);
  // Labelled photos become the ground-truth line, and the sensors' error against them.
  const waterChartData = useMemo(
    () => withTruthRows(experiment.chartData, experiment.cameraImages),
    [experiment.chartData, experiment.cameraImages]
  );
  const groundTruthSummary = useMemo(
    () => summarizeOffsets(experiment.chartData, experiment.cameraImages),
    [experiment.chartData, experiment.cameraImages]
  );
  const labelledCount = countLabelled(experiment.cameraImages);
  const labels = useMemo(() => labelSummary(experiment.cameraImages), [experiment.cameraImages]);
  const readerVsHuman = useMemo(() => readerAgreement(experiment.cameraImages), [experiment.cameraImages]);
  // The estimate's line only when this run's device reported one (an ultrasonic node never does). A boolean, so
  // the memoised lines array - and the memoised chart - only change when that flips.
  const hasLevelEstimate = useMemo(
    () => (experiment.chartData || []).some((row) => typeof row.water_level_est_cm === "number"),
    [experiment.chartData]
  );
  const waterLines = useMemo(
    () => (hasLevelEstimate ? WATER_LEVEL_LINES : WATER_LEVEL_LINES.filter((l) => l.dataKey !== "water_level_est_cm")),
    [hasLevelEstimate]
  );

  // Saves a label from the photo dialog. The dialog stays open on the updated photo; the chart's
  // photo list picks it up.
  const { updateCameraImage } = experiment;
  const labelImage = useCallback(
    async (imageId, trueLevelCm) => {
      const updated = await labelCameraImage(imageId, trueLevelCm);
      updateCameraImage(updated);
      setOpenCameraImage((current) => (current && current.id === updated.id ? updated : current));
      setWhatIfRefresh((n) => n + 1);
    },
    [updateCameraImage]
  );

  // A stable handler (not a new arrow on every render) so the memoised chart is not redrawn for it.
  const cameraImagesForClick = experiment.cameraImages;
  const openMarkerImage = useCallback(
    (marker) => setOpenCameraImage((cameraImagesForClick || []).find((image) => image.id === marker.id) || null),
    [cameraImagesForClick]
  );
  // Opens the oldest photo whose ruler reading waits for a person (the dialog has Accept / correct).
  const openImageById = useCallback(
    (id) => {
      const image = (cameraImagesForClick || []).find((item) => item.id === id);
      if (image) setOpenCameraImage(image);
      else experiment.setError("That photo belongs to another condition: pick its run in the history to label it.");
    },
    [cameraImagesForClick, experiment]
  );
  const reviewNext = useCallback(
    () => setOpenCameraImage(nextToReview(cameraImagesForClick)),
    [cameraImagesForClick]
  );
  const imageMarkers = useMemo(
    () =>
      (experiment.cameraImages || [])
        .filter((image) => image.captured_at && cameraThumbnailUrls[image.id])
        .map((image) => ({
          id: image.id,
          x: new Date(image.captured_at).getTime(),
          thumbnailUrl: cameraThumbnailUrls[image.id],
          capturedAt: image.captured_at,
          rotationDeg: image.rotation_deg,
          // A person's label, or the ruler reader's trusted reading (green); a reading waiting for review (amber).
          labelled: isLabelled(image),
          review: Boolean(truthOf(image)?.review),
          labelText: truthText(image),
        })),
    [experiment.cameraImages, cameraThumbnailUrls]
  );

  useEffect(() => {
    if (routeMac && routeMac !== selectedDevice) setSelectedDevice(routeMac);
  }, [routeMac, selectedDevice, setSelectedDevice]);

  useEffect(() => {
    if (!routeMac && selectedDevice) {
      navigate(`/devices/${encodeURIComponent(selectedDevice)}/experiment`, { replace: true });
    }
  }, [navigate, routeMac, selectedDevice]);

  // Locked while a run is active, so a browser whose saved/route device differs
  // from the run's must follow the run - otherwise the URL, and the selector it
  // drives, would keep showing a device the run isn't using.
  useEffect(() => {
    if (experiment.hasActiveRun && activeWaterMac && routeMac && routeMac !== activeWaterMac) {
      navigate(`/devices/${encodeURIComponent(activeWaterMac)}/experiment`, { replace: true });
    }
  }, [experiment.hasActiveRun, activeWaterMac, routeMac, navigate]);

  const changeWaterDevice = (mac) => {
    setSelectedDevice(mac);
    if (mac) navigate(`/devices/${encodeURIComponent(mac)}/experiment`);
  };

  if (!activeWaterMac && devicesLoading) {
    return <div className="p-6 text-slate-500">Loading authorized devices…</div>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Environment × sensor</p>
          <h1 className="mt-1 text-3xl font-extrabold text-slate-900">Experiment</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Drive a greenhouse stress profile and correlate it against this water-level sensor's own
            telemetry on one time axis.
          </p>
        </div>
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:w-[54rem] lg:grid-cols-3">
          <div>
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Water-level device</span>
            <DeviceSelector
              devices={devices.filter(
                (d) => d.mac_address !== experiment.cameraDeviceMac || d.mac_address === activeWaterMac
              )}
              value={activeWaterMac || ""}
              onChange={changeWaterDevice}
              placeholder="Select a device"
              disabled={experiment.hasActiveRun}
            />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Ground-truth camera device
            </span>
            <DeviceSelector
              devices={devices.filter(
                (d) => d.mac_address !== activeWaterMac || d.mac_address === experiment.cameraDeviceMac
              )}
              value={experiment.cameraDeviceMac || ""}
              onChange={experiment.setCameraDeviceMac}
              placeholder="None (optional)"
              disabled={experiment.hasActiveRun}
            />
            {experiment.hasActiveRun && (
              <p className="mt-1 text-[11px] text-slate-500">Locked while a run is active.</p>
            )}
          </div>
          <GreenhouseControllerSelector
            controllers={experiment.controllers}
            value={experiment.selectedMac}
            onChange={experiment.setSelectedMac}
          />
        </div>
      </header>

      {(experiment.error || exportError) && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {experiment.error || exportError}
        </div>
      )}

      {!experiment.selectedMac ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          No greenhouse controller registered yet.
        </div>
      ) : (
        <>
          <RunContextBar controller={experiment.activeController} waterDevice={waterDevice} />

          <CameraDeviceStatusCard
            mac={experiment.cameraDeviceMac}
            deviceName={cameraDevice?.name}
            status={cameraStatus.status}
            error={cameraStatus.error}
            loading={cameraStatus.loading}
          />

          <RunProgressTimeline controller={experiment.activeController} pumpConfig={experiment.pumpConfig} />

          <PumpControlPanel
            activeController={experiment.activeController}
            disabled={experiment.pumpActionPending}
            pumpConfig={experiment.pumpConfig}
            onFill={experiment.pumpFill}
            onDrain={experiment.pumpDrain}
            onStop={experiment.pumpStop}
            operations={pumpOperations.events}
            operationsError={pumpOperations.error}
            runWindow={experiment.chartWindow}
          />

          <RunHistoryList
            runs={experiment.runs}
            total={experiment.runsTotal}
            loading={experiment.runsLoading}
            viewedRunId={experiment.viewedRunId}
            onView={experiment.viewRun}
            onLoadMore={experiment.loadMoreRuns}
          />

          {calibration.campaign && (calibration.isActive || viewedCampaignId || calibration.showingLast) ? (
            <CampaignPanel
              campaign={calibration.campaign}
              error={calibration.error}
              pending={calibration.pending}
              onPause={calibration.pause}
              onResume={calibration.resume}
              onStop={calibration.stop}
              onSkip={calibration.skip}
              onExport={calibration.exportZip}
              onTestPhoto={experiment.cameraDeviceMac ? () => requestTestCapture(experiment.cameraDeviceMac) : undefined}
              isStaff={isStaff}
              deviceName={devices.find((d) => d.mac_address === calibration.campaign?.device_mac)?.name || ""}
              onClose={
                calibration.isActive ? undefined : viewedCampaignId ? experiment.followLive : calibration.dismissLast
              }
            />
          ) : null}
          {!calibration.isActive && (
            <ExperimentDesigner
              controllerMac={experiment.selectedMac}
              waterDeviceMac={activeWaterMac}
              activeController={experiment.activeController}
              profiles={experiment.profiles}
              onRefreshProfiles={experiment.refreshProfiles}
              onCreateProfile={experiment.createProfile}
              onArchiveProfile={experiment.archiveProfile}
              pumpConfig={experiment.pumpConfig}
              onStartStress={experiment.startRun}
              onStopStress={experiment.stopRun}
              disabled={experiment.actionPending}
              onCampaignStarted={(started) => {
                calibration.adopt(started);
                experiment.refresh();
              }}
            />
          )}
          <div className="flex justify-end">
            <button
              onClick={exportRun}
              disabled={!exportableRunId || exporting}
              title={exportableRunId ? "Export this run as a zip (metadata, environment/command CSVs, and PadiHAT readings if a water device is selected)" : "No run to export yet"}
              className="flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 disabled:opacity-40"
            >
              <Download className={`h-4 w-4 ${exporting ? "animate-pulse" : ""}`} />
              {exporting ? "Exporting…" : "Export this run"}
            </button>
          </div>

          {experiment.isViewingPastRun && (
            <div className="flex flex-col gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 sm:flex-row sm:items-center sm:justify-between">
              <span>
                Viewing past run <span className="font-mono font-semibold">{experiment.viewedRunId}</span> —
                not the live feed.
              </span>
              <button
                onClick={experiment.followLive}
                className="self-start rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-bold text-blue-700 sm:self-auto"
              >
                Back to live
              </button>
            </div>
          )}

          {experiment.loading ? (
            <div className="rounded-2xl bg-white p-10 text-center text-slate-500">Loading telemetry…</div>
          ) : !experiment.hasWindow ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
              Start a run to see data here.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {/* Water level first: it is what the experiment is about; the environment charts follow. */}
              <LineChartCard
                title="Water level"
                historicalData={waterChartData}
                lines={waterLines}
                syncId="experimentDashboard"
                colSpan="col-span-1 lg:col-span-2"
                connectNulls
                stepMarkers={stepMarkers}
                imageMarkers={imageMarkers}
                onImageMarkerClick={openMarkerImage}
              />
              <LineChartCard
                title="Temperature: chamber vs. PadiHAT"
                historicalData={experiment.chartData}
                lines={TEMP_LINES}
                syncId="experimentDashboard"
                colSpan="col-span-1"
                connectNulls
                stepMarkers={stepMarkers}
              />
              <LineChartCard
                title="Humidity: chamber vs. PadiHAT"
                historicalData={experiment.chartData}
                lines={HUMIDITY_LINES}
                syncId="experimentDashboard"
                colSpan="col-span-1"
                connectNulls
                stepMarkers={stepMarkers}
              />
              <GroundTruthSummary
                summary={groundTruthSummary}
                labelledCount={labelledCount}
                labels={labels}
                agreement={readerVsHuman}
                onReviewNext={reviewNext}
              />
              <CalibrationWhatIf
                source={whatIfSource}
                rows={experiment.chartData}
                images={experiment.cameraImages}
                onOpenImage={openImageById}
                refreshKey={whatIfRefresh}
              />
            </div>
          )}

          <AdHocTelemetryPanel
            controllerMac={experiment.selectedMac}
            waterDeviceMac={experiment.chartWaterDeviceMac}
            chartWindow={experiment.chartWindow}
            stepMarkers={stepMarkers}
            cameraImages={experiment.cameraImages}
          />
        </>
      )}

      <CameraImageModal image={openCameraImage} onClose={() => setOpenCameraImage(null)} onLabel={labelImage} />
    </div>
  );
}
